import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Titulo from '@components/Titulo';
import Resumen from '@components/Resumen';
import Boton from '@components/Boton';
import LoadingOverlay from '@components/LoadingOverlay';

import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { cajaContenedorStyle } from '@styles/cajaContenedor';
import { cajaContenedorPrimeroStyle } from '@styles/cajaContenedorPrimero';
import { colors } from '@styles/colors';

import { verJornadasApi } from "@api/wp/agenda/jornadas/jornadasApi"
import { verMedicosApi } from "@api/wp/agenda/medicos/medicosApi"
import { verValServApi } from "@api/wp/agenda/servicios/serviciosApi"
import { verCitaExtraApi } from "@api/wp/agenda/citasextras/citasextrasApi";
import { createMedicoBasicIfMissingApi, getMedicoDetailsByIdenApi } from "@api/wp/medicos/medicosApi";
import { API_HOST } from '@utils/const';
import { useCita } from '@providers/citaContext';

const Medicos = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cita, setCita } = useCita();

  // Obtener parámetros de la navegación
  const {
    ciudadId,
    ciudadNombre,
    especialidadNombre,
    oidEspecialidad,
    sedeId,
    sedeIdOficina,
    sedeNombre,
    fecha,
    valorPreviplan,
    sku,
    nombreServicio,
    descripcion,
    empresaSeleccionada,
    empresaArray,
  } = {
    ciudadId: cita?.ciudadId ?? location.state?.ciudadId,
    ciudadNombre: cita?.ciudadNombre ?? location.state?.ciudadNombre,
    especialidadNombre: cita?.especialidadNombre ?? location.state?.especialidadNombre,
    oidEspecialidad: cita?.oidEspecialidad ?? location.state?.oidEspecialidad,
    sedeId: cita?.sedeId ?? location.state?.sedeId,
    sedeIdOficina: cita?.sedeIdOficina ?? location.state?.sedeIdOficina,
    sedeNombre: cita?.sedeNombre ?? location.state?.sedeNombre,
    fecha: cita?.fecha ?? location.state?.fecha,
    valorPreviplan: cita?.valorPreviplan ?? location.state?.valorPreviplan,
    sku: cita?.sku ?? location.state?.sku,
    nombreServicio: cita?.nombreServicio ?? location.state?.nombreServicio,
    descripcion: (cita?.descripcion ?? location.state?.descripcion) || '',
    empresaSeleccionada: cita?.empresaSeleccionada ?? location.state?.empresaNombre,
    empresaArray: cita?.empresaArray ?? location.state?.empresaArray,
  };

  const descripcionServicio = descripcion || '';

  // Validar que el contexto de cita no esté vacío; si lo está, regresar a Home
  useEffect(() => {
    const citaVacia = !cita || (typeof cita === 'object' && Object.keys(cita).length === 0);
    if (citaVacia) {
      navigate('/home');
      return;
    }
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [medicosDisponibles, setMedicosDisponibles] = useState([]);
  const [cargandoMedicos, setCargandoMedicos] = useState(false);
  const [errorMedicos, setErrorMedicos] = useState('');
  const [medicoExpandido, setMedicoExpandido] = useState(null);
  const [citasExtras, setCitasExtras] = useState(0);
  const [medicoSeleccionado, setMedicoSeleccionado] = useState(null);
  const [horarioSeleccionado, setHorarioSeleccionado] = useState(null);
  const [modalMedicoOpen, setModalMedicoOpen] = useState(false);
  const [medicoModalInfo, setMedicoModalInfo] = useState(null);
  const [fotoModalSrc, setFotoModalSrc] = useState(null);
  // Fotos para la lista de médicos (por documento)
  const [fotosLista, setFotosLista] = useState({});
  const [detalleEspecialidad, setDetalleEspecialidad] = useState('');
  const [detalleExperiencia, setDetalleExperiencia] = useState('');
  const [detalleResenas, setDetalleResenas] = useState('');

  useEffect(() => {
    const cargarDetalles = async () => {
      try {
        const iden = medicoModalInfo?.documento_medico;
        if (!modalMedicoOpen || !iden) {
          setDetalleEspecialidad('');
          setDetalleExperiencia('');
          setDetalleResenas('');
          return;
        }
        const resp = await getMedicoDetailsByIdenApi(String(iden));
        const r = resp?.result || {};
        setDetalleEspecialidad(r.especialidad || '');
        setDetalleExperiencia(r.experiencia || '');
        setDetalleResenas(r.resenas || '');
      } catch (_) {
        setDetalleEspecialidad('');
        setDetalleExperiencia('');
        setDetalleResenas('');
      }
    };
    cargarDetalles();
  }, [modalMedicoOpen, medicoModalInfo]);

  // Helper: derivar bases y probar HEAD
  const getBaseUrls = () => {
    const bases = [];
    if (API_HOST) {
      bases.push(API_HOST.replace(/\/$/, '').replace(/\/api$/, ''));
    }
    bases.push(window.location.origin);
    bases.push('http://localhost:3000');
    return Array.from(new Set(bases));
  };

  const tryHead = async (url) => {
    try {
      const res = await fetch(url, { method: 'HEAD' });
      return res.ok;
    } catch {
      return false;
    }
  };

  const resolveFotoUrlByIden = async (iden) => {
    if (!iden) return null;
    const sanitized = String(iden).trim().replace(/[^\dA-Za-z_-]/g, '');
    const exts = ['jpg', 'jpeg', 'png', 'webp'];
    const bases = getBaseUrls();
    for (const base of bases) {
      for (const ext of exts) {
        const url = `${base}/public/medicos/${sanitized}.${ext}`;
        if (await tryHead(url)) return url;
      }
    }
    return null;
  };

  // Prefetch de fotos para la lista cuando cambia medicosDisponibles
  useEffect(() => {
    let cancelled = false;
    const prefetch = async () => {
      const updates = {};
      for (const m of medicosDisponibles) {
        const iden = m?.documento_medico;
        if (!iden) continue;
        // Evitar reprocesar si ya existe valor (incluye null)
        if (Object.prototype.hasOwnProperty.call(fotosLista, iden)) continue;
        const url = await resolveFotoUrlByIden(iden);
        updates[iden] = url || null;
      }
      if (!cancelled && Object.keys(updates).length > 0) {
        setFotosLista(prev => ({ ...prev, ...updates }));
      }
    };
    prefetch();
    return () => { cancelled = true; };
  }, [medicosDisponibles]);

  useEffect(() => {
    if (sedeId && oidEspecialidad && fecha) {
      obtenerMedicosDisponibles();
    }
  }, [ciudadId, oidEspecialidad, sedeId, fecha]);

  const obtenerMedicosDisponibles = async () => {
    setCargandoMedicos(true);
    setErrorMedicos('');
    setMedicosDisponibles([]);

    try {
      const requ = {
        "empresa": empresaSeleccionada,
        "oid_sede": sedeId,
        "oid_especialidad": oidEspecialidad,
        "fecha": fecha
      };
      const response = await verMedicosApi(requ);
      if (response && response.data) {

        // Para cada médico, consultar verCitaExtra primero y luego sus jornadas si hay resultado
        const medicosConJornadas = await Promise.all(
          response.data.map(async (medico) => {
            try {
              // Consultar verCitaExtra con los parámetros requeridos
              const citaExtraReq = {
                "oid_especialidad": oidEspecialidad,
                "fecha": fecha,
                "oid_medico": medico.documento_medico,// Iden del doctor para verCitaExtra
                // Usar id real de oficina_sede para verCitaExtra
                "oid_sede": sedeIdOficina,
              };
              console.log('REQ CITAS EXTRA ', citaExtraReq )
              const citaExtraResponse = await verCitaExtraApi(citaExtraReq);
              console.log('RESPO CITAS EXTRA 1 ', citaExtraResponse )
              // Si verCitaExtra devuelve resultado, consultar jornadas              
              if (citaExtraResponse && citaExtraResponse.result && citaExtraResponse.result.length > 0) {
                const req = {
                  "empresa": empresaSeleccionada,
                  "oid_medico": medico.oid_medico,
                  "fecha": fecha
                }
                const jornadaResponse = await verJornadasApi(req);
                return {
                  ...medico,
                  jornada: jornadaResponse?.data || [],
                  tieneAgenda: true
                };
              } else {
                // Si no hay resultado en verCitaExtra, marcar como sin agenda
                return {
                  ...medico,
                  jornada: [],
                  tieneAgenda: false
                };
              }
            } catch (error) {
              console.error(`Error al consultar agenda para médico ${medico.oid_medico}:`, error);
              return {
                ...medico,
                jornada: [],
                tieneAgenda: false
              };
            }
          })
        );
        setMedicosDisponibles(medicosConJornadas || []);
      } else {
        setMedicosDisponibles([]);
      }

    } catch (error) {
      console.error('Error al obtener médicos disponibles:', error);
      setErrorMedicos('Error al cargar los médicos disponibles');
    } finally {
      setCargandoMedicos(false);
    }
  };

  // Cargar foto para el modal usando documento_medico como nombre de archivo
  const cargarFotoModal = async (documento_medico) => {
    try {
      const iden = String(documento_medico || '').trim();
      if (!iden) { setFotoModalSrc(null); return; }
      // Derivar bases sin "/api" y probar alternativas de puerto y origen
      const API_BASE = String(API_HOST).replace(/\/(api)$/i, '');
      const bases = [API_BASE];
      if (/localhost:3001/i.test(API_BASE)) bases.push(API_BASE.replace(':3001', ':3000'));
      if (typeof window !== 'undefined' && window.location?.origin && !bases.includes(window.location.origin)) {
        bases.push(window.location.origin);
      }
      const exts = ['jpg', 'jpeg', 'png', 'webp'];

      for (const base of bases) {
        for (const ext of exts) {
          const url = `${base}/public/medicos/${iden}.${ext}`;
          try {
            const resp = await fetch(url, { method: 'HEAD' });
            if (resp.ok) { setFotoModalSrc(url); return; }
          } catch (_) { /* seguir probando */ }
        }
      }
      setFotoModalSrc(null);
    } catch (_) {
      setFotoModalSrc(null);
    }
  };

  const seleccionarHorario = (medico, horario) => {
    setCita(prev => ({
      ...prev,
      ciudadId,
      ciudadNombre,
      especialidadNombre,
      nombreServicio,
      descripcion: descripcionServicio,
      oidEspecialidad,
      sedeId,
      sedeNombre,
      fecha,
      empresaSeleccionada,
      empresaArray,
      medicoSeleccionado: medico.oid_medico,
      medicoNombre: medico.nombre_medico,
      documento_medico: medico.documento_medico,
      horarioSeleccionado: horario.oid_turno,
      valorPreviplan,
      sku,
    }));
    navigate('/agenda/horas');
  };
  return (
    <div id="cajaPrincipal"
      style={cajaPrincipalStyle}
    >
      <Titulo titulo="Escoge tu profesional" subtitulo="Selecciona el profesional y la jornada disponible para tu atención."
        imagen_titulo="medi"
        rutaRetorno="/fechas"
      ></Titulo>

      <LoadingOverlay visible={cargandoMedicos} />

      {/* Div central */}
      <div style={{...cajaContenedorStyle, ...(isMobile && { marginLeft: '0', width: '100%', padding: '0 2vh', boxSizing: 'border-box' })}}>
        {/* Primer div */}

        {/* Contenido del primer div */}
        <div style={{...cajaContenedorPrimeroStyle, ...(isMobile && { width: '100%', marginLeft: '0', display: 'flex', flexDirection: 'column', alignItems: 'center' })}}>

          {/* Lista de médicos */}
          <div id="cajaMedicos" style={{
            marginTop: '3vh',
            marginLeft: '0vh',
            width: isMobile ? '100%' : '69.5vh',
            maxHeight: '50vh',
            overflowY: 'auto',
            paddingBottom: '2vh'
          }}>
            {cargandoMedicos ? (
              <div style={{
                textAlign: 'center',
                padding: '4vh',
                color: colors.gray600,
                fontFamily: 'Inter',
                fontSize: '1.6vh'
              }}>
                <p>Cargando médicos disponibles...</p>
              </div>
            ) : errorMedicos ? (
              <div style={{
                textAlign: 'center',
                padding: '4vh',
                color: colors.errorText,
                backgroundColor: colors.errorShadow,
                borderRadius: '8px'
              }}>
                <p>{errorMedicos}</p>
              </div>
            ) : medicosDisponibles.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '4vh',
                color: colors.gray600
              }}>
                <p>No se encontraron médicos disponibles para esta fecha.</p>
              </div>
            ) : (
              <ul id="cajaMedicosLista" style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                gap: '2vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: colors.gray200,
                borderRadius: '1vh',
                height: '45.3vh',
                overflowY: 'auto'
              }}>
                {medicosDisponibles.map((medico, index) => (
                  <li key={medico.oid_medico} style={{
                    width: isMobile ? 'calc(100% - 3vh)' : '66.5vh',
                    minHeight: medicoExpandido === medico.oid_medico ? '20vh' : '9.7vh',
                    padding: '1.5vh 2.0vh',
                    margin: '0 1.5vh 1vh 1.5vh',
                    ...(index === 0 ? { marginTop: '1.5vh' } : {}),
                    ...(medicoExpandido === medico.oid_medico ? { marginTop: '1.5vh' } : {}),
                    opacity: 1,
                    borderRadius: '1.0vh',
                    borderWidth: '0.1vh',
                    borderColor: colors.gray200,
                    backgroundColor: medicoExpandido === medico.oid_medico ? colors.white : colors.white,
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    fontFamily: 'Inter'
                  }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      height: '9.7vh',
                      cursor: 'pointer'
                    }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      console.log('Médico seleccionado:', medico);
                      // Crear médico básico si falta solo cuando se expande
                      if (medicoExpandido !== medico.oid_medico) {
                        try {
                          await createMedicoBasicIfMissingApi({
                            oid_medico: medico?.oid_medico,
                            nombre: medico?.nombre_medico,
                            iden: medico?.documento_medico
                          });
                        } catch (err) {
                          console.warn('No se pudo insertar médico básico (expandir):', err?.message || err);
                        }
                      }
                      setMedicoExpandido(medicoExpandido === medico.oid_medico ? null : medico.oid_medico);
                    }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          width: '7.3vh',
                          height: '7.3vh',
                          minWidth: '7.3vh',
                          minHeight: '7.3vh',
                          maxWidth: '7.3vh',
                          maxHeight: '7.3vh',
                          aspectRatio: '1',
                          flexShrink: 0,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          backgroundColor: fotosLista[medico?.documento_medico] ? 'transparent' : colors.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                        }}>
                          {fotosLista[medico?.documento_medico] ? (
                            <img
                              src={fotosLista[medico?.documento_medico]}
                              alt="Foto médico"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center',
                                display: 'block',
                              }}
                              onError={(e) => {
                                e.currentTarget.src = '/avatar.png';
                                const iden = medico?.documento_medico;
                                if (iden) {
                                  setFotosLista(prev => ({ ...prev, [iden]: null }));
                                }
                              }}
                            />
                          ) : (
                            <img
                              src="/avatar.png"
                              alt="Sin foto"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center',
                                display: 'block',
                              }}
                            />
                          )}
                        </div>
                        <span style={{
                          fontSize: '1.8vh',
                          fontWeight: '500',
                          color: colors.gray700,
                          marginLeft: '1.2vh'
                        }}>
                          {medico.nombre_medico}
                        </span>
                      </div>
                      <div id="expandir"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <div id="verInfo"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            marginRight: '1.5vh'
                          }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            // Crear médico básico si falta al abrir modal
                            try {
                              await createMedicoBasicIfMissingApi({
                                oid_medico: medico?.oid_medico,
                                nombre: medico?.nombre_medico,
                                iden: medico?.documento_medico
                              });
                            } catch (err) {
                              console.warn('No se pudo insertar médico básico (modal):', err?.message || err);
                            }
                            setMedicoModalInfo(medico);
                            setModalMedicoOpen(true);
                            // Usar la foto ya cargada de la lista
                            const fotoDeLista = fotosLista[medico?.documento_medico];
                            setFotoModalSrc(fotoDeLista || null);
                          }}
                          title="Ver detalle"
                        >
                          <img
                            src="/infoMed.png"
                            alt="Ver detalle"
                            style={{ width: '3vh', height: '3vh' }}
                          />
                        </div>
                        <img
                          src={medicoExpandido === medico.oid_medico ? '/ok.png' : '/ok2.png'}
                          alt={medicoExpandido === medico.oid_medico ? 'Contraer' : 'Expandir'}
                          style={{ width: '3vh', height: '3vh' }}
                        />
                      </div>
                    </div>

                    {medicoExpandido === medico.oid_medico && (
                      <div style={{
                        padding: '1vh 0',
                        marginTop: '1vh',
                        marginLeft: '5vh'
                      }}>
                        <h4 style={{
                          margin: '0 0 1vh 0',
                          fontSize: '1.6vh',
                          fontWeight: '700',
                          color: colors.primaryText
                        }}>Jornada</h4>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '1vh'
                        }}>
                          {!medico.tieneAgenda ? (
                            <div style={{
                              color: colors.warnIcon,
                              fontWeight: '600',
                              fontSize: '1.4vh',
                              backgroundColor: colors.warnBg,
                              padding: '1vh 2vh',
                              borderRadius: '1vh',
                              border: `1px solid ${colors.warnIcon}`
                            }}>
                              Sin citas extras disponibles para el día {fecha}
                            </div>
                          ) : medico.jornada && medico.jornada.length > 0 ? (
                            medico.jornada.map((horario, index) => {
                              const seleccionado =
                                medicoSeleccionado?.oid_medico === medico.oid_medico &&
                                horarioSeleccionado?.oid_turno === horario.oid_turno;
                              const bg = seleccionado ? colors.verdeClaro : colors.gray150;
                              const bord = seleccionado ? colors.verdeClaro : colors.gray150;
                              return (
                                <div id="jornadaHorario"
                                  key={index}
                                  style={{
                                    minWidth: '15vh',
                                    height: '4.3vh',
                                    backgroundColor: bg,
                                    border: `1px solid ${bord}`,
                                    borderRadius: '5vh',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '2vh',
                                    fontWeight: '500',
                                    padding: '0 1vh'
                                  }}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setMedicoSeleccionado(medico);
                                    setHorarioSeleccionado(horario);
                                  }}
                                >
                                  <img
                                    src={seleccionado ? '/jornada1.png' : '/jornada2.png'}
                                    alt={seleccionado ? 'Jornada seleccionada' : 'Jornada disponible'}
                                    style={{ width: '3vh', height: '3vh', marginRight: '0.6vh' }}
                                  />
                                  <span style={{ color: colors.gray600, fontSize: '2vh', }}>
                                    {horario.franja_horaria || 'Sin horario'}
                                  </span>
                                </div>
                              );
                            })
                          ) : (
                            <div style={{
                              color: colors.warnIcon,
                              fontWeight: '600',
                              fontSize: '1.4vh',
                              backgroundColor: colors.warnBg,
                              padding: '1vh 2vh',
                              borderRadius: '1vh',
                              border: `1px solid ${colors.warnIcon}`
                            }}>
                              Sin agenda disponible
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div id="cajaSiguiente" style={{
            display: 'flex',
            flexDirection: 'column',
            width: isMobile ? '100%' : '80vh',
            marginTop: '1vh',
            gap: '2vh',
            justifyContent: isMobile ? 'center' : 'flex-end',
            alignItems: isMobile ? 'center' : 'flex-end',
            position: 'relative',
          }}>
            <Boton
              label={"Continuar"}
              onClick={() => {
                if (medicoSeleccionado && horarioSeleccionado) {
                  seleccionarHorario(medicoSeleccionado, horarioSeleccionado);
                }
              }}
              siguiente
              ancho={isMobile ? '100%' : '23vh'}
              alto="5.2vh"
              disabled={!(medicoSeleccionado && horarioSeleccionado)}
            />
          </div>
        </div>
        {/* Fin contenido del primer div */}

        {!isMobile && <Resumen ciudad={ciudadNombre} servicio={especialidadNombre} ubicacion={sedeNombre} fecha={fecha} nomServicio={nombreServicio} descripcion={descripcionServicio} />}
      </div>

      {/* Modal detalle de médico */}
      {modalMedicoOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            width: isMobile ? '90%' : '69vh',
            height: isMobile ? 'auto' : '57.8vh',
            maxHeight: '90%',
            backgroundColor: colors.white,
            border: `1.5vh solid ${colors.gray600}`,
            borderRadius: '3vh',
            boxShadow: '0 0.4vh 1.2vh rgba(0,0,0,0.2)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            padding: '2vh',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontFamily: 'Inter', color: colors.gray700 }}>
 
               </h3>
               <button onClick={() => {
                setModalMedicoOpen(false);
                setFotoModalSrc(null);
               }} style={{
                 backgroundColor: 'transparent',
                 border: 'none',
                 color: colors.accent,
                 cursor: 'pointer',
                 fontSize: '2vh'
               }}>✕</button>
            </div>
            <div style={{ flex: 1, marginTop: '2vh', overflowY: 'auto' }}>
              {/* Contenido del modal: placeholder */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                columnGap: '2vh',
                alignItems: 'start',
                width: '100%'
              }}>

                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '1.8vh', marginTop: '1.8vh' }}>
                  <span style={{
                    fontSize: '3.2vh',
                    fontWeight: '600',
                    fontFamily: 'Inter',
                    color: colors.gray600
                  }}>
                    {medicoModalInfo?.nombre_medico || 'Detalle del médico'}
                  </span>
                  <span style={{
                    fontSize: '2vh',
                    fontWeight: '400',
                    fontFamily: 'Inter',
                    color: colors.gray700,
                    marginTop: '1vh'
                  }}>
                    {detalleEspecialidad || 'Especialidad no disponible'}
                  </span>
                  <span style={{
                    fontSize: '2vh',
                    fontWeight: '400',
                    color: colors.gray700,
                    fontFamily: 'Inter',
                    marginTop: '2.0vh'
                  }}>
                    {detalleExperiencia ? `Experiencia profesional: ${detalleExperiencia}` : 'Experiencia profesional no disponible'}
                  </span>
                  <span style={{
                    fontSize: '2vh',
                    fontWeight: '700',
                    color: colors.gray700,
                    fontFamily: 'Inter',
                    marginTop: '2.0vh'
                  }}>
                    Perfil profesional
                  </span>
                  <p style={{
                    fontSize: '1.4vh',
                    fontWeight: '400',
                    color: colors.gray600,
                    fontFamily: 'Inter',
                    marginTop: '1vh',
                    marginBottom: 0
                  }}>
                    {detalleResenas || 'Reseña no disponible'}
                  </p>
                </div>

                <div id="cajaAvatar" style={{
                  width: '17vh',
                  height: '17vh',
                  minWidth: '17vh',
                  minHeight: '17vh',
                  maxWidth: '17vh',
                  maxHeight: '17vh',
                  aspectRatio: '1',
                  flexShrink: 0,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: `0.3vh solid ${colors.primary}`,
                  backgroundColor: colors.gray200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '4vh',
                  boxSizing: 'border-box',
                }}>
                  <img
                    src={fotoModalSrc || '/avatar.png'}
                    alt="Avatar"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block',
                    }}
                  />
                </div>

              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Medicos;



